/** Falls back to 'de-DE' when unavailable (e.g., Node.js). */
const getSystemLocale = (): string => {
	if (typeof navigator !== 'undefined' && navigator.language) {
		return navigator.language
	}
	return 'de-DE'
}

export class Numbers {
	/** For display formatting; use an explicit locale like 'de-DE' for exports. */
	readonly systemLocale = getSystemLocale()

	roundNumber(number: number, decimalPlaces: number = 2): number {
		const factor = Math.pow(10, decimalPlaces)
		return Math.round(number * factor) / factor
	}

	/**
	 * Uses the user's system locale by default for display formatting.
	 *
	 * @example
	 * // Uses system locale (e.g., user's browser setting)
	 * numbers.formatNumber(1234.56)
	 *
	 * // Explicit locale for exports (bank systems expect German format)
	 * numbers.formatNumber(1234.56, 'de-DE', { useGrouping: false })
	 */
	formatNumber(number: number, locale: string = this.systemLocale, options: Intl.NumberFormatOptions = {}): string {
		return new Intl.NumberFormat(locale, options).format(number)
	}

	parseToPureNumber(numberString: string, decimalSeparator: string = ','): number {
		const normalizedString = numberString.replace(decimalSeparator, '.')
		return parseFloat(normalizedString)
	}

	/** Rounds then formats — composes roundNumber() + formatNumber(). */
	doIt(
		number: number,
		decimalPlaces: number = 2,
		locale: string = this.systemLocale,
		options: Intl.NumberFormatOptions = {},
	): string {
		const roundedNumber = this.roundNumber(number, decimalPlaces)
		return this.formatNumber(roundedNumber, locale, options)
	}

	formatCurrency(amount: number, currency: string = '€'): string {
		return `${currency}${this.doIt(amount)}`
	}

	formatDelta(delta: number, currency: string = '€'): string {
		const symbol = delta > 0 ? '↑' : delta < 0 ? '↓' : '→'
		return `${symbol} ${currency}${this.doIt(Math.abs(delta))}`
	}

	getDeltaClass(delta: number): string {
		return delta > 0 ? 'text-error-default' : delta < 0 ? 'text-primary-default' : 'text-neutral'
	}

	/** 1.5 → "1 1/2", 2.25 → "2 1/4". */
	toMixedFraction(number: number, precision: number = 16, maxDenominator: number = 4): string {
		const isNegative = number < 0
		number = Math.abs(number)

		const wholePart = Math.floor(number)

		let decimalPart = number - wholePart

		// If the decimal part is very small or zero, just return the whole number
		if (decimalPart < 1 / Math.pow(10, precision)) {
			return isNegative ? `-${wholePart}` : `${wholePart}`
		}

		const { numerator, denominator } = this.decimalToFraction(decimalPart, precision, maxDenominator)

		if (wholePart === 0) {
			return isNegative ? `-${numerator}/${denominator}` : `${numerator}/${denominator}`
		} else {
			return isNegative ? `-${wholePart} ${numerator}/${denominator}` : `${wholePart} ${numerator}/${denominator}`
		}
	}

	private decimalToFraction(
		decimal: number,
		precision: number,
		maxDenominator: number = 4,
	): { numerator: number; denominator: number } {
		if (decimal === 0) {
			return { numerator: 0, denominator: 1 }
		}

		// Initialize best approximation
		let bestError = Infinity
		let bestNumerator = 0
		let bestDenominator = 1

		// Check common fractions first for better user experience
		// Filter to only include fractions with denominators <= maxDenominator
		const commonFractions = [
			{ n: 1, d: 2 }, // 1/2
			{ n: 1, d: 3 }, // 1/3
			{ n: 2, d: 3 }, // 2/3
			{ n: 1, d: 4 }, // 1/4
			{ n: 3, d: 4 }, // 3/4
		]

		// Add additional fractions only if maxDenominator allows
		const additionalFractions = []
		if (maxDenominator >= 5) {
			additionalFractions.push(
				{ n: 1, d: 5 }, // 1/5
				{ n: 2, d: 5 }, // 2/5
				{ n: 3, d: 5 }, // 3/5
				{ n: 4, d: 5 }, // 4/5
			)
		}
		if (maxDenominator >= 6) {
			additionalFractions.push(
				{ n: 1, d: 6 }, // 1/6
				{ n: 5, d: 6 }, // 5/6
			)
		}
		if (maxDenominator >= 8) {
			additionalFractions.push(
				{ n: 1, d: 8 }, // 1/8
				{ n: 3, d: 8 }, // 3/8
				{ n: 5, d: 8 }, // 5/8
				{ n: 7, d: 8 }, // 7/8
			)
		}
		if (maxDenominator >= 10) {
			additionalFractions.push(
				{ n: 1, d: 10 }, // 1/10
				{ n: 3, d: 10 }, // 3/10
				{ n: 7, d: 10 }, // 7/10
				{ n: 9, d: 10 }, // 9/10
			)
		}
		if (maxDenominator >= 12) {
			additionalFractions.push(
				{ n: 1, d: 12 }, // 1/12
				{ n: 5, d: 12 }, // 5/12
				{ n: 7, d: 12 }, // 7/12
				{ n: 11, d: 12 }, // 11/12
			)
		}
		if (maxDenominator >= 16) {
			additionalFractions.push(
				{ n: 1, d: 16 }, // 1/16
				{ n: 3, d: 16 }, // 3/16
				{ n: 5, d: 16 }, // 5/16
				{ n: 7, d: 16 }, // 7/16
				{ n: 9, d: 16 }, // 9/16
				{ n: 11, d: 16 }, // 11/16
				{ n: 13, d: 16 }, // 13/16
				{ n: 15, d: 16 }, // 15/16
			)
		}

		const allFractions = [...commonFractions, ...additionalFractions]

		for (const frac of allFractions) {
			if (frac.d <= maxDenominator) {
				const error = Math.abs(decimal - frac.n / frac.d)
				if (error < bestError) {
					bestError = error
					bestNumerator = frac.n
					bestDenominator = frac.d

					// If we're very close to a common fraction, just use it
					if (error < 1 / Math.pow(10, precision)) {
						return { numerator: frac.n, denominator: frac.d }
					}
				}
			}
		}

		// If no suitable common fraction found, use a more sophisticated approach
		// for denominators up to maxDenominator
		for (let d = 1; d <= maxDenominator; d++) {
			const n = Math.round(decimal * d)
			const error = Math.abs(decimal - n / d)

			if (error < bestError) {
				bestError = error
				bestNumerator = n
				bestDenominator = d
			}
		}

		// Only use continued fraction expansion if we're significantly off
		// and maxDenominator allows for larger denominators
		if (bestError > 1 / Math.pow(10, precision / 2) && maxDenominator > 4) {
			// Continued fraction expansion
			let n1 = 1,
				n2 = 0
			let d1 = 0,
				d2 = 1
			let b = decimal

			do {
				let a = Math.floor(b)
				let aux = n1
				n1 = a * n1 + n2
				n2 = aux

				aux = d1
				d1 = a * d1 + d2
				d2 = aux

				b = 1 / (b - a)

				const currentError = Math.abs(decimal - n1 / d1)

				if (currentError < 1 / Math.pow(10, precision) || d1 > maxDenominator) {
					if (d1 > maxDenominator) {
						return { numerator: bestNumerator, denominator: bestDenominator }
					}
					return { numerator: n1, denominator: d1 }
				}
			} while (b !== Infinity)

			bestNumerator = n1
			bestDenominator = d1
		}

		const gcd = this.findGCD(bestNumerator, bestDenominator)
		return {
			numerator: bestNumerator / gcd,
			denominator: bestDenominator / gcd,
		}
	}

	private findGCD(a: number, b: number): number {
		return b === 0 ? a : this.findGCD(b, a % b)
	}

	formatMixedFraction(
		number: number,
		format: 'unicode' | 'ascii' | 'superscript' = 'ascii',
		precision: number = 16,
		maxDenominator: number = 4,
	): string {
		const basicFraction = this.toMixedFraction(number, precision, maxDenominator)

		if (format === 'ascii') {
			return basicFraction
		}

		const isNegative = basicFraction.startsWith('-')
		const withoutSign = isNegative ? basicFraction.substring(1) : basicFraction

		const hasMixedPart = withoutSign.includes(' ')

		if (!withoutSign.includes('/')) {
			return basicFraction
		}

		let wholePart = ''
		let fractionPart = withoutSign

		if (hasMixedPart) {
			const parts = withoutSign.split(' ')
			wholePart = parts[0]
			fractionPart = parts[1]
		}

		const [numerator, denominator] = fractionPart.split('/')

		if (format === 'unicode') {
			const unicodeFraction = this.getUnicodeFraction(parseInt(numerator), parseInt(denominator))

			if (unicodeFraction) {
				return isNegative
					? `-${wholePart}${hasMixedPart ? ' ' : ''}${unicodeFraction}`
					: `${wholePart}${hasMixedPart ? ' ' : ''}${unicodeFraction}`
			}

			return basicFraction
		}

		if (format === 'superscript') {
			const superNumerator = this.toSuperscript(numerator)
			const subDenominator = this.toSubscript(denominator)

			if (hasMixedPart) {
				return isNegative
					? `-${wholePart} ${superNumerator}⁄${subDenominator}`
					: `${wholePart} ${superNumerator}⁄${subDenominator}`
			} else {
				return isNegative ? `-${superNumerator}⁄${subDenominator}` : `${superNumerator}⁄${subDenominator}`
			}
		}

		return basicFraction
	}

	private getUnicodeFraction(numerator: number, denominator: number): string | null {
		const fractionMap: Record<string, string> = {
			'1/4': '¼',
			'1/2': '½',
			'3/4': '¾',
			'1/3': '⅓',
			'2/3': '⅔',
			'1/5': '⅕',
			'2/5': '⅖',
			'3/5': '⅗',
			'4/5': '⅘',
			'1/6': '⅙',
			'5/6': '⅚',
			'1/7': '⅐',
			'1/8': '⅛',
			'3/8': '⅜',
			'5/8': '⅝',
			'7/8': '⅞',
			'1/9': '⅑',
			'1/10': '⅒',
		}

		const key = `${numerator}/${denominator}`
		return fractionMap[key] || null
	}

	private toSuperscript(str: string): string {
		const superscriptMap: Record<string, string> = {
			'0': '⁰',
			'1': '¹',
			'2': '²',
			'3': '³',
			'4': '⁴',
			'5': '⁵',
			'6': '⁶',
			'7': '⁷',
			'8': '⁸',
			'9': '⁹',
			'-': '⁻',
		}

		return str
			.split('')
			.map(char => superscriptMap[char] || char)
			.join('')
	}

	private toSubscript(str: string): string {
		const subscriptMap: Record<string, string> = {
			'0': '₀',
			'1': '₁',
			'2': '₂',
			'3': '₃',
			'4': '₄',
			'5': '₅',
			'6': '₆',
			'7': '₇',
			'8': '₈',
			'9': '₉',
			'-': '₋',
		}

		return str
			.split('')
			.map(char => subscriptMap[char] || char)
			.join('')
	}
}

const numbers = new Numbers()

export default numbers
